import { useId } from "react";
import {
  Card,
  VField,
  VFileUpload,
  VInput,
  VRadioGroup,
  VTextarea,
  type VFileUploadFile,
  type VRadioOption,
} from "@/components/ui";
import type {
  DeploymentLocation,
  PolePresence,
  RequestPhoto,
} from "@/domains/requests/api/types";

const POLE_OPTIONS: ReadonlyArray<VRadioOption<PolePresence>> = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Neen" },
  { value: "needsToBePlaced", label: "Moet er een paal komen" },
  { value: "notApplicable", label: "Niet van toepassing" },
];

type StepLocationProps = {
  value: DeploymentLocation;
  onChange: (next: DeploymentLocation) => void;
};

export function StepLocation({ value, onChange }: StepLocationProps): React.JSX.Element {
  const poleName = useId();
  const patch = (partial: Partial<DeploymentLocation>): void => onChange({ ...value, ...partial });

  const handleAddFiles = (files: ReadonlyArray<File>): void => {
    const newPhotos: ReadonlyArray<RequestPhoto> = files.map((file, idx) => ({
      id: `ph-${Date.now()}-${idx}`,
      filename: file.name,
      url: URL.createObjectURL(file),
    }));
    onChange({ ...value, photos: [...value.photos, ...newPhotos] });
  };

  const handleRemovePhoto = (id: string): void => {
    onChange({ ...value, photos: value.photos.filter((p) => p.id !== id) });
  };

  const uploadFiles: ReadonlyArray<VFileUploadFile> = value.photos.map((p) => ({
    id: p.id,
    name: p.filename,
    size: 0,
    previewUrl: p.url,
  }));

  return (
    <Card padding="lg" className="space-y-4">
      <VField
        label="12. Adres"
        htmlFor="address"
        required
        hint="Geef het precieze adres of een zo precies mogelijke beschrijving van de locatie."
      >
        <VInput
          id="address"
          value={value.address}
          onChange={(e) => patch({ address: e.target.value })}
        />
      </VField>

      <VField
        label="13. Locatie en kijkrichting"
        htmlFor="cameraDirection"
        required
        hint="Specifieer zo duidelijk mogelijk de exacte locatie waar de camera geplaatst moet worden, alsook de kijkrichting."
      >
        <VTextarea
          id="cameraDirection"
          rows={3}
          value={value.locationAndCameraDirection}
          onChange={(e) => patch({ locationAndCameraDirection: e.target.value })}
        />
      </VField>

      <VField label="14. Aanwezigheid paal" required>
        <VRadioGroup
          name={poleName}
          value={value.polePresence}
          options={POLE_OPTIONS}
          onChange={(v) => patch({ polePresence: v })}
        />
      </VField>

      <VField
        label="15. Plaatselijke foto's plaatsing"
        hint="Voeg minimum 2 foto's toe van de aanvankelijke situatie. Zo kan de plaatsingsfirma de plaatsbaarheid inschatten."
      >
        <VFileUpload
          files={uploadFiles}
          onAdd={handleAddFiles}
          onRemove={handleRemovePhoto}
        />
      </VField>

      <VField
        label="16. Zijn er omgevingsfactoren of bijzonderheden waarmee rekening dient gehouden te worden?"
        htmlFor="environmental"
      >
        <VTextarea
          id="environmental"
          rows={3}
          value={value.environmentalFactors}
          onChange={(e) => patch({ environmentalFactors: e.target.value })}
        />
      </VField>
    </Card>
  );
}
